/** The design system's segmented control, as real controls.
 *
 *  `components/forms/SegmentedTabs.jsx` renders its options as
 *  `<span onClick>` — the fourth component with a handler and no semantics,
 *  after CaptureBar, TabBar and SheetAction. Unreachable by keyboard, unnamed to
 *  a screen reader, and it silently did nothing when driven programmatically.
 *
 *  Reproduces its visual spec — a hairline box, the active option filled with
 *  the accent, mono label plus an optional count — using a tablist. Delete this
 *  when the component grows semantics.
 */
export function SegmentedNav<T extends string>({
  options,
  active,
  onSelect,
  label,
  variant = 'filled',
}: {
  options: { value: T; label: string; count?: number }[];
  active: T;
  onSelect: (value: T) => void;
  label: string;
  /** `filled` for a standalone control; `underline` where the tabs head a page
   *  and a filled box would compete with the title; `boxed` for a state filter
   *  sitting under a title, which the filtered task list frame draws as separate
   *  outlined boxes rather than one joined bar. */
  variant?: 'filled' | 'underline' | 'boxed';
}) {
  if (variant === 'underline') {
    return (
      <div
        role="tablist"
        aria-label={label}
        style={{ display: 'flex', gap: 'var(--bujo-space-6)', borderBottom: 'var(--bujo-border-hairline)' }}
      >
        {options.map((option) => {
          const current = option.value === active;
          return (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={current}
              onClick={() => onSelect(option.value)}
              style={{
                minHeight: 'var(--bujo-tap-min)',
                cursor: 'pointer',
                background: 'none',
                // A 2px amber rule under the active tab, sitting on the
                // container's own hairline.
                boxShadow: current ? 'inset 0 -2px 0 var(--bujo-accent)' : 'none',
                color: current ? 'var(--bujo-accent)' : 'var(--bujo-text-meta)',
                font: `${current ? 'var(--bujo-fw-bold)' : 'var(--bujo-fw-regular)'} var(--bujo-fs-label)/1 var(--bujo-font-mono)`,
                letterSpacing: 'var(--bujo-ls-label)',
              }}
            >
              {option.label}
              {option.count === undefined ? null : ` · ${option.count}`}
            </button>
          );
        })}
      </div>
    );
  }

  if (variant === 'boxed') {
    return (
      <div
        role="tablist"
        aria-label={label}
        style={{
          display: 'flex',
          gap: 'var(--bujo-space-2)',
          // Four state labels do not fit 280px of content box. Wrapping keeps
          // every option reachable without the page scrolling sideways, which
          // is the one layout failure with no acceptable version.
          flexWrap: 'wrap',
        }}
      >
        {options.map((option) => {
          const current = option.value === active;
          return (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={current}
              onClick={() => onSelect(option.value)}
              style={{
                minHeight: 'var(--bujo-row-min)',
                // Tight: `OPEN n DONE n MIGRATED n CANCELLED n` plus the label
                // letter-spacing is a few pixels over 350px at space-3, which
                // wrapped CANCELLED onto its own line at 390. The frame shows
                // all four on one line there.
                padding: '0 var(--bujo-space-2)',
                cursor: 'pointer',
                background: 'none',
                border: `1px solid ${current ? 'var(--bujo-accent)' : 'var(--bujo-rule-hairline)'}`,
                color: current ? 'var(--bujo-accent)' : 'var(--bujo-text-meta)',
                font: `${current ? 'var(--bujo-fw-bold)' : 'var(--bujo-fw-regular)'} var(--bujo-fs-label)/1 var(--bujo-font-mono)`,
                letterSpacing: 'var(--bujo-ls-label)',
              }}
            >
              {option.label}
              {option.count === undefined ? null : ` ${option.count}`}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div role="tablist" aria-label={label} style={{ display: 'flex', border: 'var(--bujo-border-hairline)' }}>
      {options.map((option, index) => {
        const current = option.value === active;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={current}
            onClick={() => onSelect(option.value)}
            style={{
              flex: 1,
              // 44px, the documented floor, not a stretching minimum: letting
              // these stretch made the control taller than the title above it.
              // It was 40px on the reasoning that a full-width row is an easy
              // target anyway — true of the width, irrelevant to the height,
              // and 4px under the bar the brief sets.
              height: 44,
              cursor: 'pointer',
              borderLeft: index === 0 ? 'none' : 'var(--bujo-border-hairline)',
              background: current ? 'var(--bujo-accent)' : 'transparent',
              color: current ? 'var(--bujo-cal-ink)' : 'var(--bujo-text-meta)',
              font: `${current ? 'var(--bujo-fw-bold)' : 'var(--bujo-fw-regular)'} var(--bujo-fs-label)/1 var(--bujo-font-mono)`,
              letterSpacing: 'var(--bujo-ls-label)',
            }}
          >
            {option.label}
            {option.count === undefined ? null : (
              <span style={{ opacity: current ? 0.7 : 1 }}> {option.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
